import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Breadcrumbs, Link, Alert } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Assessment as AssessmentIcon } from '@mui/icons-material';
import { useReportPermissions } from '../../hooks/useReportPermissions';

// مكون التخطيط العام لصفحات التقارير
const ReportsLayout: React.FC = () => {
  const location = useLocation();
  const { hasReportPagePermission } = useReportPermissions();
  const [currentPage, setCurrentPage] = useState<string>("");
  const [hasAccess, setHasAccess] = useState<boolean>(true);
  
  // تحقق من صلاحيات الوصول للصفحة الحالية
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(segment => segment);
    let pageId = "";
    
    // تحديد نوع التقرير
    if (pathSegments.includes('inventory-display')) {
      pageId = 'inventory-display-report';
    } else if (pathSegments.includes('inventory-movement')) {
      pageId = 'inventory-movement';
    } else if (pathSegments.includes('product-movement')) {
      pageId = 'product-movement-report';
    } else if (pathSegments.includes('product-profit')) {
      pageId = 'product-profit-report';
    } else if (pathSegments.includes('product-prices')) {
      pageId = 'product-prices';
    } else if (pathSegments.includes('category-quantities')) {
      pageId = 'category-quantities-report';
    } else if (pathSegments.includes('commissions')) {
      pageId = 'sales-commissions';
    } else if (pathSegments.includes('inventory')) {
      pageId = 'inventory';
    }
    
    // حفظ معرف الصفحة الحالية
    setCurrentPage(pageId);
    
    // التحقق من صلاحيات الوصول فقط إذا كان هناك صفحة محددة
    if (pageId) {
      // التحقق من الصلاحية
      const accessGranted = hasReportPagePermission(pageId, 'view_page');
      console.log(`Checking ${pageId} permission: ${accessGranted}`);
      setHasAccess(accessGranted);
    } else {
      setHasAccess(true);
    }
  }, [location.pathname, hasReportPagePermission]);

  // تحديد العنوان والمسار التفصيلي بناء على المسار الحالي
  const getBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(segment => segment);
    
    // نص العنوان والمسار التفصيلي المناسب حسب المسار الحالي
    if (pathSegments.includes('inventory-display')) {
      return {
        title: 'عرض كميات المخزون',
        breadcrumbs: [
          { name: 'التقارير', path: '/reports' },
          { name: 'عرض كميات المخزون', path: '/reports/inventory-display' }
        ]
      };
    } else if (pathSegments.includes('inventory-movement')) {
      return {
        title: 'حركة المخزون',
        breadcrumbs: [
          { name: 'التقارير', path: '/reports' },
          { name: 'حركة المخزون', path: '/reports/inventory-movement' }
        ]
      };
    } else if (pathSegments.includes('product-movement')) {
      return {
        title: 'تقرير حركة المنتجات',
        breadcrumbs: [
          { name: 'التقارير', path: '/reports' },
          { name: 'تقرير حركة المنتجات', path: '/reports/product-movement' }
        ]
      };
    } else if (pathSegments.includes('product-profit')) {
      return {
        title: 'تقرير ربح المنتجات',
        breadcrumbs: [
          { name: 'التقارير', path: '/reports' },
          { name: 'تقرير ربح المنتجات', path: '/reports/product-profit' }
        ]
      };
    } else if (pathSegments.includes('product-prices')) {
      return {
        title: 'تقرير أسعار المنتجات',
        breadcrumbs: [
          { name: 'التقارير', path: '/reports' },
          { name: 'تقرير أسعار المنتجات', path: '/reports/product-prices' }
        ]
      };
    } else if (pathSegments.includes('category-quantities')) {
      return {
        title: 'عرض كميات التصنيفات',
        breadcrumbs: [
          { name: 'التقارير', path: '/reports' },
          { name: 'عرض كميات التصنيفات', path: '/reports/category-quantities' }
        ]
      };
    } else if (pathSegments.includes('commissions')) {
      return {
        title: 'تقرير العمولات البيعية',
        breadcrumbs: [
          { name: 'التقارير', path: '/reports' },
          { name: 'تقرير العمولات البيعية', path: '/reports/commissions' }
        ]
      };
    } else if (pathSegments.includes('inventory')) {
      return {
        title: 'تقرير المخزون',
        breadcrumbs: [
          { name: 'التقارير', path: '/reports' },
          { name: 'تقرير المخزون', path: '/reports/inventory' }
        ]
      };
    } else {
      return {
        title: 'التقارير',
        breadcrumbs: [
          { name: 'التقارير', path: '/reports' }
        ]
      };
    }
  };

  const { title, breadcrumbs } = getBreadcrumbs();

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      {/* عنوان الصفحة */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AssessmentIcon 
          sx={{ 
            mr: 1.5, 
            color: 'primary.main', 
            fontSize: '2rem' 
          }} 
        />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
      </Box>

      {/* مسار التنقل التفصيلي */}
      <Breadcrumbs 
        aria-label="مسار التنقل" 
        sx={{ mb: 3, '& .MuiBreadcrumbs-ol': { justifyContent: 'flex-start' } }}
      >
        <Link 
          component={RouterLink} 
          to="/" 
          underline="hover" 
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          الرئيسية
        </Link>
        {breadcrumbs.map((breadcrumb, index) => (
          <Link
            key={index}
            component={RouterLink}
            to={breadcrumb.path}
            underline="hover"
            color={index === breadcrumbs.length - 1 ? 'primary' : 'inherit'}
            sx={{ fontWeight: index === breadcrumbs.length - 1 ? 'bold' : 'normal' }}
          >
            {breadcrumb.name}
          </Link>
        ))}
      </Breadcrumbs>

      {/* محتوى الصفحة */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          borderRadius: 2,
          overflow: 'hidden',
          minHeight: '70vh',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
        }}
      >
        {!hasAccess && currentPage ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6" component="div" sx={{ mb: 1 }}>
              عفواً، ليس لديك صلاحية الوصول لهذه الصفحة
            </Typography>
            <Typography variant="body1">
              الرجاء التواصل مع مدير النظام للحصول على الصلاحيات المطلوبة.
            </Typography>
          </Alert>
        ) : (
          <Outlet />
        )}
      </Paper>
    </Box>
  );
};

export default ReportsLayout;